import type { CategoryContent } from './row.js';

export const THEORETICAL_PHYSICS: CategoryContent = {
  bscba: [
    [
      'In quantum mechanics, what does the Born rule establish?',
      [
        'A probability equals the squared magnitude of the amplitude',
        'Energy and momentum are always conserved in quantum interactions',
        'Electrons exist as particles rather than probability waves',
        'The position of a particle can only be known to one decimal place',
      ],
      0,
      'The Born rule connects the wave function to measurement probabilities. It is a fundamental postulate relating amplitudes to observable outcomes.',
    ],
    [
      'What does Einstein\'s mass-energy relation predict about light?',
      [
        'Photons with higher frequency carry proportionally more energy',
        'Light travels slower through transparent materials because it gains mass',
        'All types of radiation have identical energy regardless of frequency',
        'Electromagnetic waves cannot exert momentum on matter',
      ],
      0,
      'E=mc² shows that frequency determines photon energy: E=hf. Higher frequency light carries more energy per photon.',
    ],
    [
      'In special relativity, what happens to time for an object moving near light speed?',
      [
        'Time passes more slowly for the moving object',
        'Time stops completely once the object reaches half light speed',
        'Time always moves at the same rate for all observers regardless of motion',
        'The moving object experiences time speeding up proportionally to its velocity',
      ],
      0,
      'Time dilation is a core prediction of special relativity: moving clocks run slower. This effect grows more pronounced at higher speeds.',
    ],
    [
      'What principle prevents two identical electrons from occupying the same quantum state?',
      [
        'The Pauli exclusion principle',
        'The uncertainty principle',
        'The correspondence principle',
        'The equivalence principle',
      ],
      0,
      'The Pauli exclusion principle forbids identical fermions (like electrons) from sharing all quantum numbers. This explains atomic structure and chemistry.',
    ],
    [
      'In quantum tunneling, how does a particle cross a barrier higher than its total energy?',
      [
        'The wave function stays non-zero past the barrier',
        'The particle slows down temporarily to gather extra kinetic energy',
        'It doesn\'t cross; the barrier always reflects quantum particles',
        'The particle splits into smaller pieces that individually have enough energy',
      ],
      0,
      'Tunneling occurs because the wave function doesn\'t abruptly stop at a barrier. It decays exponentially but remains non-zero beyond the barrier edge.',
    ],
    [
      'What is the primary difference between bosons and fermions?',
      [
        'Fermions have half-integer spin; bosons have integer spin',
        'Bosons are heavier than all fermions',
        'Fermions move faster than bosons',
        'Only bosons can be detected by laboratory instruments',
      ],
      0,
      'Spin is the defining distinction: fermions have spin 1/2, 3/2, etc.; bosons have spin 0, 1, 2, etc. This fundamentally determines their statistical behavior.',
    ],
    [
      'How does the Heisenberg uncertainty principle restrict simultaneous knowledge of conjugate variables?',
      [
        'Position and momentum uncertainty has a minimum non-zero product',
        'Either position or momentum is always exactly zero for any particle',
        'Velocity and acceleration cannot both be measured on the same day',
        'Measuring one property destroys the particle, preventing measurement of the other',
      ],
      0,
      'Δx·Δp ≥ ℏ/2 shows position and momentum cannot both be arbitrarily precise. This is fundamental to quantum mechanics, not an experimental limitation.',
    ],
    [
      'What does superposition in quantum mechanics allow?',
      [
        'A system exists in multiple states until measured',
        'A particle can be in two different locations at the same instant',
        'An electron can have two different velocities measured at once',
        'Energy can be both created and destroyed without conservation',
      ],
      0,
      'Superposition means a quantum state is a linear combination of basis states. Upon measurement, the system collapses to one outcome with specific probabilities.',
    ],
    [
      'In general relativity, what does spacetime curvature represent?',
      [
        'Mass and energy curve the geometry of space and time',
        'A visual representation that has no physical significance',
        'The speed at which light travels through a region',
        'The strength of the weak nuclear force in a region',
      ],
      0,
      'Curvature replaces the Newtonian concept of gravity as a force. Mass warps spacetime geometry, and particles follow geodesic paths through curved space.',
    ],
    [
      'What is the Compton effect evidence for?',
      [
        'Light behaves as particles with energy set by frequency',
        'Electrons always move at the speed of light',
        'Matter cannot reflect electromagnetic radiation',
        'Photons increase in frequency when absorbed by electrons',
      ],
      0,
      'Compton scattering shows photons transfer momentum like particles: scattered photons have longer wavelengths. This confirmed the photon concept.',
    ],
    [
      'How does quantization arise in Bohr\'s atomic model?',
      [
        'Angular momentum can only take discrete integer multiples of ℏ',
        'Electrons are confined to spherical shells with no intermediate orbits',
        'Atoms can only emit light at specific, discrete frequencies',
        'The nucleus attracts electrons with a force that increases with distance',
      ],
      0,
      'Bohr postulated that angular momentum L=nℏ where n is an integer. This quantization explains discrete energy levels and spectral lines.',
    ],
    [
      'What is wave-particle duality?',
      [
        'Matter and energy show both wave and particle behavior',
        'Particles and waves are fundamentally separate entities that never interact',
        'Particles are made of many tiny waves vibrating together',
        'Only light exhibits wave behavior; matter is purely particulate',
      ],
      0,
      'Objects like electrons show particle properties (localization, momentum) and wave properties (diffraction, interference). Context determines which behavior dominates.',
    ],
    [
      'What does the principle of relativity state in special relativity?',
      [
        'Physics is the same in all inertial reference frames',
        'All reference frames experience identical time intervals',
        'Velocity is relative, but acceleration is absolute',
        'The speed of light varies depending on the observer\'s motion',
      ],
      0,
      'Einstein\'s first postulate: physical laws have identical form for all inertial observers. Combined with constant light speed, this yields time dilation and length contraction.',
    ],
    [
      'What is antimatter?',
      [
        'Particles identical to ordinary matter but opposite in charge',
        'A hypothetical form of matter that repels regular matter at all distances',
        'The empty space between atoms and electrons',
        'A supersymmetric partner of ordinary matter that only exists in accelerators',
      ],
      0,
      'Antimatter (positrons, antiprotons, etc.) has opposite charge but equal mass. Particle-antiparticle pairs annihilate, converting mass entirely to energy.',
    ],
    [
      'In quantum field theory, what are quantum fields?',
      [
        'Fields that permeate space; particles are their excitations',
        'Mathematical artifacts used only for convenience in calculations',
        'Physical forces that directly push particles from one place to another',
        'Regions of space where the uncertainty principle does not apply',
      ],
      0,
      'Fields are the basic ontology of QFT. Particles emerge as quanta—excitations above the ground state. Every particle type corresponds to a unique field.',
    ],
  ],
  msc: [
    [
      'What does the Bell test determine about local hidden variable theories?',
      [
        'They cannot reproduce all quantum mechanical predictions',
        'They provide a complete description of quantum mechanics at the subatomic scale',
        'They are mathematically equivalent to quantum mechanics but easier to compute',
        'They predict a fixed value for particle properties before measurement occurs',
      ],
      0,
      'Bell\'s theorem shows local realism contradicts QM predictions for entangled state measurements. Experiments violate Bell inequalities, ruling out local hidden variables.',
    ],
    [
      'What role does the Lagrangian play in classical and quantum field theory?',
      [
        'It yields equations of motion and conserved quantities',
        'It encodes the probability distribution of particle positions',
        'It directly measures the total energy content of a system',
        'It specifies the boundary conditions that particles must satisfy',
      ],
      0,
      'The Lagrangian density generates the theory: Hamilton\'s principle and Noether\'s theorem yield field equations and conservation laws directly from its structure.',
    ],
    [
      'In the Dirac equation, what physical property does the fourth component of the spinor represent?',
      [
        'The amplitude for antimatter (negative energy) solutions',
        'The probability of finding the electron at a given location',
        'The z-component of the electron\'s orbital angular momentum',
        'The coupling strength to the electromagnetic field',
      ],
      0,
      'The Dirac equation naturally includes negative energy solutions, interpreted as antiparticles (positrons). The four-component spinor accommodates both.',
    ],
    [
      'What does renormalization address in quantum field theory?',
      [
        'Divergent loop integrals absorbed into redefined parameters',
        'The fact that measurements always yield finite results while theory predicts infinite values',
        'The need to modify quantum mechanics at very short distances',
        'How to convert the Lagrangian into a Hamiltonian formalism',
      ],
      0,
      'Loop integrals diverge at high energies, but coupling constants and masses run with scale. Renormalization extracts finite, scale-dependent predictions from divergent expressions.',
    ],
    [
      'What is the physical significance of gauge symmetry in field theory?',
      [
        'It predicts the existence of gauge bosons (force carriers)',
        'It ensures that all particles in the theory move at the speed of light',
        'It eliminates the need to specify boundary conditions in calculations',
        'It proves that all forces reduce to gravity at sufficient energy scales',
      ],
      0,
      'Gauge symmetries require the introduction of gauge fields (photons, gluons, W/Z bosons). Local symmetry principles underpin the Standard Model.',
    ],
    [
      'What does the Higgs mechanism accomplish?',
      [
        'Gives mass to gauge bosons and fermions via symmetry breaking',
        'Explains why certain particles decay faster than others',
        'Provides a unified description of the weak and electromagnetic interactions',
        'Predicts the mass of the electron with high precision',
      ],
      0,
      'Spontaneous electroweak symmetry breaking via the Higgs field gives mass to W/Z bosons and fermions. The Higgs vev (246 GeV) sets the electroweak scale.',
    ],
    [
      'What does the coupling constant in quantum field theory control?',
      [
        'The strength of interaction between particles',
        'The total momentum carried by all particles in a system',
        'The rate at which particles decay into lighter products',
        'The number of spatial dimensions accessible to the theory',
      ],
      0,
      'Coupling strength (α for QED, αs for QCD) governs interaction probabilities. Weak couplings permit perturbative calculations; strong couplings require non-perturbative methods.',
    ],
    [
      'In statistical mechanics, what is the Boltzmann distribution?',
      [
        'The probability of a state is proportional to exp(-E/kT)',
        'It proves that entropy always increases in isolated systems',
        'It describes how particles move randomly in gases',
        'It relates temperature to the average kinetic energy of particles',
      ],
      0,
      'P(E)∝exp(-E/kT) governs thermal equilibrium. Systems explore states weighted by Boltzmann factors, reproducing observed thermodynamic behavior.',
    ],
    [
      'What distinguishes quantum chromodynamics from quantum electrodynamics?',
      [
        'QCD has a non-abelian SU(3) symmetry group with confinement',
        'QED describes massive particles while QCD only describes massless ones',
        'QCD applies only to the strong nuclear force, while QED applies everywhere',
        'QED predicts infinite divergences; QCD avoids them through additional symmetries',
      ],
      0,
      'QCD\'s SU(3) gauge symmetry is non-commutative: gluons self-interact. This causes asymptotic freedom (weak at short range) and confinement (quarks unbindable singly).',
    ],
    [
      'What is asymptotic freedom?',
      [
        'The coupling weakens at shorter distances in theories like QCD',
        'The ability of particles to escape from a potential well with only kinetic energy',
        'The property that all particles eventually decay into stable end products',
        'The principle that no force can accelerate a particle beyond light speed',
      ],
      0,
      'Asymptotic freedom (discovered by Gross, Politzer, Wilczek) means the strong interaction weakens at small distances. This is why quarks are nearly free inside hadrons.',
    ],
    [
      'What is a path integral in Feynman formulation?',
      [
        'A sum over all trajectories weighted by phase factors',
        'The classical trajectory that minimizes the action functional',
        'An integral over momentum space that computes scattering cross sections',
        'The set of Feynman diagrams that contribute to a process',
      ],
      0,
      'The path integral ∫𝒟φ e^(iS/ℏ) sums contributions from all field configurations. Classical paths dominate; quantum interference between them encodes probability.',
    ],
    [
      'What is spontaneous symmetry breaking?',
      [
        'When the ground state of a system has less symmetry than its Lagrangian',
        'When a Lagrangian terms violate a known conservation law',
        'When quantum fluctuations destroy the exact symmetry of a classical field',
        'When external forces externally perturb a system out of equilibrium',
      ],
      0,
      'If the potential minima don\'t respect Lagrangian symmetries, the vacuum is asymmetric. The system "chooses" one direction, breaking symmetry spontaneously.',
    ],
    [
      'What does the CPT theorem guarantee?',
      [
        'Physical laws are unchanged under combined C, P, and T',
        'Energy and momentum are always conserved in quantum interactions',
        'Particles and antiparticles have identical lifetimes and decay modes',
        'The speed of light is the same in all reference frames',
      ],
      0,
      'CPT symmetry (derived from causality and Lorentz invariance) is a fundamental theorem in local QFT. Violations would indicate exotic physics.',
    ],
    [
      'What is the running coupling constant?',
      [
        'The coupling strength that changes with the energy scale',
        'A constant that appears in the coupling of particles to external fields',
        'The rate at which coupling strength degrades due to particle decay',
        'A parameter that varies in time during the evolution of a system',
      ],
      0,
      'The coupling strength g(μ) depends on the momentum scale μ. QED\'s runs to smaller values at high energies; QCD\'s runs to larger values at low energies.',
    ],
    [
      'In the Standard Model, what role do the W and Z bosons play?',
      [
        'They mediate the weak nuclear force',
        'They carry the strong force between quarks inside hadrons',
        'They bind electrons to nuclei in atoms',
        'They transmit gravitational attraction between massive objects',
      ],
      0,
      'The W±and Z bosons mediate flavor-changing weak interactions. Their large mass (~80-91 GeV) makes weak interactions short-range, giving nuclei stability.',
    ],
  ],
  phd: [
    [
      'What does the renormalization group equation (beta function) quantify in asymptotically free theories?',
      [
        'The rate of change of the coupling constant with energy scale',
        'The probability distribution for particle positions in a quantum field',
        'The rate at which virtual particles are created and annihilated in loops',
        'The relationship between mass and coupling strength in a theory',
      ],
      0,
      'β(g)=dg/dln(μ) governs running. For QCD, β<0 at high q², driving asymptotic freedom. Fixed points structure conformal window and critical behavior.',
    ],
    [
      'What physical mechanism underlies quark confinement in QCD?',
      [
        'Color flux tubes have tension; separating quarks creates new pairs',
        'The strong force has infinite range, binding quarks permanently through a potential',
        'Quarks are actually composite objects made of smaller, even more confined constituents',
        'Confinement is an approximation that breaks down at very high temperatures',
      ],
      0,
      'QCD\'s non-abelian structure permits conformal flux tubes. Work to separate quarks asymptotically approaches energy to produce pairs; singlets form instead.',
    ],
    [
      'What is the physical meaning of a topological term in a Lagrangian?',
      [
        'It contributes to the action but not the equations of motion',
        'It describes particles that exist only on the boundary of a system',
        'It encodes information about the Coulomb potential in the theory',
        'It is an artifact of regularization that disappears in the continuum limit',
      ],
      0,
      'Topological terms (θ-angle in QCD) are total derivatives; they alter action by 2πn under large transformations. They affect vacuum structure without classical eom.',
    ],
    [
      'How do anomalies in quantum field theory arise, and what do they signify?',
      [
        'Classical symmetries are broken at the quantum level by loop diagrams',
        'They are calculation errors that disappear with correct regularization schemes',
        'They indicate that a classical symmetry is no longer exact after quantization',
        'They prove that a theory is inconsistent and must be abandoned',
      ],
      0,
      'Anomalies emerge when fermionic loops break classical symmetries. The triangle diagram (ABB anomaly) is central. Anomaly matching constrains unified theories.',
    ],
    [
      'In effective field theory, what determines the power counting and organization of operators?',
      [
        'The scaling dimension of operators relative to the cutoff',
        'The total number of loops in Feynman diagrams contributing to a process',
        'Whether a symmetry is explicitly broken or spontaneously broken in the UV theory',
        'The mass hierarchy of particles in the underlying theory',
      ],
      0,
      'EFT organizing principle: suppress high-dimension operators (∼Λ^(-n)). This yields systematic predictions valid below the cutoff, with convergent expansions.',
    ],
    [
      'What is the significance of the Calabi-Yau manifold in string theory?',
      [
        'Its geometry encodes the compactified extra dimensions',
        'It is a solution to Einstein\'s equations describing black holes in extra dimensions',
        'It proves that string theory is the only consistent quantum gravity',
        'It demonstrates that supersymmetry must be exact at all energy scales',
      ],
      0,
      'Calabi-Yau (CY) threefolds provide consistent string compactifications preserving N=1 supersymmetry at low energies. Topology determines generations and interactions.',
    ],
    [
      'What role does the operator product expansion (OPE) play in conformal field theory?',
      [
        'It decomposes nearby operator products into sums of local operators',
        'It relates two-point functions to four-point functions through crossing equations',
        'It proves that all correlation functions are determined by a single coupling constant',
        'It shows that conformal dimensions must be integers',
      ],
      0,
      'OPE: O₁(x)O₂(0)~∑ Cᵢ(x)|x|^(Δ₁+Δ₂-Δᵢ) Oᵢ(0). Conformal invariance determines OPE coefficients through constraints; it structures the entire theory.',
    ],
    [
      'How does the AdS/CFT correspondence provide insight into strongly-coupled gauge theories?',
      [
        'It maps a strongly-coupled CFT to a weakly-coupled gravity theory',
        'It proves that gravity and gauge theory are fundamentally identical',
        'It shows that all quantum field theories are equivalent to string theories',
        'It eliminates the need for renormalization in quantum field theory',
      ],
      0,
      'AdS/CFT (Maldacena): large-N gauge theory ↔ string gravity. Strong coupling in CFT ↔ weak coupling in gravity. Enables non-perturbative calculations via duality.',
    ],
    [
      'What is meant by asymptotic safety in quantum gravity?',
      [
        'An ultraviolet fixed point of the gravitational coupling',
        'A proof that gravity must be screened by matter at high energies',
        'The statement that gravity decouples from other interactions above the Planck scale',
        'A mechanism preventing the graviton from appearing in scattering processes',
      ],
      0,
      'Asymptotic safety (Weinberg): g(μ)→g* as μ→∞. A non-trivial UV fixed point can render gravity renormalizable. Remains unproven but viable.',
    ],
    [
      'What does the Zamolodchikov c-theorem establish in two-dimensional field theories?',
      [
        'A function that decreases monotonically along RG flows',
        'That all two-dimensional theories must have conformal symmetry in the UV',
        'That coupling constants can never increase along any physical evolution',
        'That entropy production must always equal the anomaly coefficient',
      ],
      0,
      'c-theorem: central charge decreases monotonically under RG evolution: cᵤᵥ > cᵢᵣ. Arrows time\'s direction at RG level; no return to higher c.',
    ],
    [
      'What is the physical origin of black hole entropy in string theory?',
      [
        'Degeneracy of microstates near the event horizon',
        'The entanglement entropy between the black hole interior and exterior',
        'A deep symmetry principle that requires entropy proportional to surface area',
        'Quantum corrections to the Einstein-Hilbert action near the singularity',
      ],
      0,
      'String theory counts black hole microstates via D-branes. Degeneracy density reproduces S_BH=A/(4G) exactly for certain black holes, confirming holography.',
    ],
    [
      'How do instanton solutions in gauge theory illuminate non-perturbative physics?',
      [
        'Topologically non-trivial configurations that tunnel between vacua',
        'They are artifacts of perturbation theory that vanish in the exact solution',
        'They represent virtual particles produced in loop diagrams',
        'They prove that all coupling constants must be rational numbers',
      ],
      0,
      'Instantons: finite-action configurations interpolating between vacua differing by a winding number. They give non-perturbative contributions to amplitudes; crucial for theta dependence.',
    ],
    [
      'What is the significance of moduli in string theory compactifications?',
      [
        'Scalar fields from geometric deformations of the compactification',
        'Particles that mediate the weak nuclear force in the compactified theory',
        'Extra dimensions that cannot be accessed by any physical experiments',
        'Mathematical parameters that affect predictions but are unobservable in principle',
      ],
      0,
      'Moduli (size, shape) parameterize the compactification space. Their vevs fix the low-energy gauge group, matter content, and couplings. Moduli stabilization is crucial.',
    ],
    [
      'What role do superconformal algebras play in supersymmetric theories?',
      [
        'They extend the conformal group with supersymmetric generators',
        'They prove that supersymmetry must be spontaneously broken at low energies',
        'They eliminate all quantum corrections to the Lagrangian',
        'They guarantee that all couplings remain finite at all energy scales',
      ],
      0,
      'Superconformal symmetry (SU(2,2|N) in 4D) includes both conformal and SUSY generators. It constrains anomalies; all F-terms must vanish in SCFT.',
    ],
    [
      'What is the beta function for a scalar coupling λ in φ⁴ theory at one loop?',
      [
        'β(λ)=(3λ²)/(16π²) at one loop',
        'A negative quantity that drives the coupling toward zero at all energy scales',
        'Zero to all loop orders, making φ⁴ a conformal theory',
        'Proportional to the renormalized mass, not the coupling itself',
      ],
      0,
      'One-loop: β=(3λ²)/(16π²)>0. The coupling runs to larger values at high energies (trivial fixed point is UV repulsive). λ develops a Landau pole (theory breaks down).',
    ],
  ],
  professor: [
    [
      'What is the swampland conjecture, and why is it important for string theory phenomenology?',
      [
        'Most gravity-consistent EFTs lack a UV completion in string theory',
        'It proves that all quantum field theories can be embedded into string theory',
        'It demonstrates that supersymmetry must be broken at energies below the weak scale',
        'It eliminates the need for dark matter in cosmological models',
      ],
      0,
      'Swampland conjectures (e.g., distance conjecture, dS conjecture) restrict low-energy EFTs. They constrain inflation, quintessence, and moduli spaces in realistic string models.',
    ],
    [
      'How do the anomaly polynomial and Green-Schwarz mechanism ensure 10D superstring consistency?',
      [
        'The anomaly polynomial factorizes under specific conditions',
        'They eliminate the need for supersymmetry in the 10-dimensional theory',
        'They prove that gauge groups must be U(1) factors only',
        'They require all fermions to have identical masses',
      ],
      0,
      'Anomaly polynomial must vanish for consistency. GS mechanism: X₈ Tr(R²)Tr(F²) cancels via B∧X terms. Fixes gauge group (SO(32) or E₈×E₈); essential for 10D validity.',
    ],
    [
      'What is the resurgence structure in asymptotic series, and how does it relate to trans-series in field theory?',
      [
        'An asymptotic series encodes information about non-perturbative corrections',
        'A mathematical coincidence that allows perturbative calculations to reproduce non-perturbative results',
        'A symmetry that ensures the coupling constant is invariant under transformations',
        'A mechanism that eliminates the need for renormalization in certain theories',
      ],
      0,
      'Resurgence: trans-series structure transmutes asymptotic series into exact results via Borel resummation. Instantons, renormalons appear as discontinuities; relates weak-strong duality.',
    ],
    [
      'How does the Sachdev-Ye-Kitaev model illuminate quantum gravity in the large-N limit?',
      [
        'A solvable random-coupling fermion model with a gravity dual',
        'It proves that gravity is purely classical and requires no quantum treatment',
        'It demonstrates that entropy always increases in quantum systems',
        'It shows that holography applies only to theories with large gauge groups',
      ],
      0,
      'SYK (large q random couplings): exact, soluble in large-N. CFT dual exists; chaos exponent λₗ=π/β matches black holes. Paradigm for AdS₂/CFT₁.',
    ],
    [
      'What is M-theory, and what evidence supports its existence as a unified framework for string dualities?',
      [
        'An 11-dimensional theory conjectured to underlie all superstring theories',
        'A purely mathematical construction with no physical relevance to particle physics',
        'A specific type of string theory that includes all known particle physics',
        'A mechanism that proves gravity and quantum mechanics are incompatible',
      ],
      0,
      'M-theory: 11D SUGRA at low energies. Type II/E₈×E₈/SO(32) strings are its limits under different compactifications. Web of dualities (T, S, U) connects all.',
    ],
    [
      'How do you calculate the soft graviton theorem and what does it reveal about scattering amplitudes?',
      [
        'Soft graviton limits factor into simple universal factors',
        'By taking the limit where all external particles approach the speed of light',
        'Using only tree-level Feynman diagrams without any loop corrections',
        'It demonstrates that gravitons are fundamentally different from other particles',
      ],
      0,
      'Soft theorem: A_soft(k→0)→Sₖ·A, where Sₖ is universal. Reveals asymptotic BMS symmetries. Loop extensions: soft/collinear limits constrain IR structure of gravity.',
    ],
    [
      'What is the meaning of a replica trick calculation in entanglement entropy, and what are its limitations?',
      [
        'It analytically continues Zₙ=Tr(ρ^n) from n→1 to extract Sₑ',
        'A method that only applies to free field theories without interactions',
        'A way to compute entropy that works for all quantum systems without exception',
        'A technique that proves entanglement entropy is always proportional to volume',
      ],
      0,
      'Replica: Zₙ as path integral on n-sheeted manifold; continue to n→1. Works for free/weakly coupled; strong coupling may have new saddles (higher genus).',
    ],
    [
      'How do brane dynamics and DBI actions capture non-perturbative string effects in D-brane systems?',
      [
        'The DBI action governs D-brane motion in dynamical background fields',
        'They describe how open strings emit closed strings in scattering processes',
        'They prove that D-branes cannot exist in consistent string theories',
        'They show that supersymmetry is exact in all brane configurations',
      ],
      0,
      'DBI: √det(g+F); includes backreaction, string corrections. Tachyons signal instability; brane-antibrane annihilation liberates radiation. Non-perturbative dynamics.',
    ],
    [
      'What is the Hirzebruch signature theorem, and how does it constrain anomalies in spinor electrodynamics?',
      [
        'The signature of a manifold equals the index of a Dirac-type operator',
        'A result showing that all curved spaces must have positive scalar curvature',
        'A theorem that eliminates the need for renormalization in field theory',
        'A statement that all manifolds have identical topological properties',
      ],
      0,
      'Signature Thm: τ(M)=∫L-genus. In spinors on M⁴: chiral anomaly (8π²)⁻¹∫ Tr(F²)=η(boundary term). Relates gauge-gravity coupling to topology.',
    ],
    [
      'How do the Regge limit and BFKL equation govern high-energy scattering in QCD?',
      [
        's→∞ at fixed momentum transfer exhibits Regge behavior',
        'They only apply to electromagnetic scattering, not to strong interactions',
        'They prove that all scattering cross sections decrease with energy',
        'They eliminate the need for perturbation theory in QCD calculations',
      ],
      0,
      'Regge: elastic scattering~sᵅ(t) exchanges. BFKL: resum ln(s) chains, gives αₚ~1.2>1 pomeron. Dominates small-x DIS and pp→dijets at LHC.',
    ],
    [
      'What is the holomorphic anomaly in topological string theory, and what structure does it reveal?',
      [
        'F-terms lose holomorphy at genus>0 due to worldsheet instantons',
        'A sign that topological strings are not consistently defined',
        'A mechanism that makes all topological correlators zero above tree level',
        'A symmetry that protects string theory from quantum corrections',
      ],
      0,
      'Holomorphic anomaly: ∂F_g/∂t̄≠0 at genus g. Determines recursion; FG instantons correct F(t). Crucial for computing mirror-symmetric Gromov-Witten invariants.',
    ],
    [
      'How do lattice QCD simulations at finite baryon density encounter the sign problem, and what insights does it provide about QCD phase transitions?',
      [
        'The fermionic determinant becomes complex at μ≠0',
        'Lattice spacing must be reduced infinitely to access finite density',
        'The sign problem is purely a computational artifact with no physical meaning',
        'It demonstrates that the QCD phase diagram is identical at all densities',
      ],
      0,
      'Sign problem: det(D+μ)→complex, breaks importance sampling. Solutions: Taylor-expansion μ, analytic continuation from imaginary μ, functional RG. Maps QCD phase diagram.',
    ],
    [
      'What is the meaning of F-theory as a geometric approach to string compactifications, and how does it relate to heterotic duality?',
      [
        'F-theory is a 12D formalism using elliptic fibration geometry',
        'A theory that requires more than 11 fundamental dimensions',
        'A method that eliminates the need for all other string theory dualities',
        'A proof that gravity decouples from gauge interactions at low energies',
      ],
      0,
      'F-theory: type IIB on K3 fibers (τ varies). Elliptic curve structure→gauge groups, matter via monodromy. Heterotic dual: E₈ bundle on K3. Geometric unification.',
    ],
    [
      'How do infrared divergences in gravity differ from those in Yang-Mills theory, and what is soft theorems\' role in their cancellation?',
      [
        'Gravity has both soft photon and soft graviton divergences',
        'Gravity has no infrared divergences due to the long range of gravitational force',
        'Yang-Mills and gravity have identical infrared structures with no distinction',
        'Soft theorems prove that all infrared divergences can be completely eliminated',
      ],
      0,
      'Gravity IR: soft graviton & photon. Soft thm: A_soft(E→0)→Sₖ·A reduces divergence degree. Still divergent (unlike EM in 4D), but structured; asymptotic symmetries BMS.',
    ],
    [
      'What does the generalized entropy (Wald entropy) predict for black hole thermodynamics in higher-curvature gravity?',
      [
        'S=∫ dA·∂L/∂R_abcd, correcting Bekenstein-Hawking for general Lagrangians',
        'It proves that black hole entropy is always exactly A/(4G) regardless of theory',
        'It shows that black holes violate the second law of thermodynamics',
        'It eliminates the need for quantum mechanics to understand black holes',
      ],
      0,
      'Wald: S=(1/4G)∫ dA·√h·(∂L/∂R_abcd)ε^ab_cd. At horizon, R terms contribute; alters κ, ε. For Gauss-Bonnet: S∝(A+βA²). Relevant for high-E string scale.',
    ],
  ],
};
